import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "capexara_cookie_consent";
const CONSENT_VERSION = 1;

const DEFAULT_CHOICES = {
  essential: true,   // always on, can't be toggled off
  analytics: false,
  marketing: false,
};

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function write(categories) {
  const payload = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: { ...DEFAULT_CHOICES, ...categories, essential: true },
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // Fire-and-forget telemetry — anonymous decision log (no personal data).
  // Does not block the UI; failures are silent (e.g., visitor offline).
  try {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const cats = payload.categories;
    let choice = "custom";
    if (cats.analytics && cats.marketing) choice = "accept_all";
    else if (!cats.analytics && !cats.marketing) choice = "reject_non_essential";
    fetch(`${BACKEND}/api/consent-events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ choice, analytics: !!cats.analytics, marketing: !!cats.marketing }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
  // Notify other tabs / listeners
  window.dispatchEvent(new CustomEvent("capexara:consent-change", { detail: payload }));
  return payload;
}

/**
 * Cookie consent hook.
 * Returns:
 *   consent: stored payload (null = user has not chosen yet)
 *   accepted: convenience boolean per category, from current state
 *   acceptAll() / rejectNonEssential() / save(categories) / reset()
 */
export function useConsent() {
  const [consent, setConsent] = useState(() => read());

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail || read());
    window.addEventListener("capexara:consent-change", onChange);
    // Sync across tabs
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setConsent(read());
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("capexara:consent-change", onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const acceptAll = useCallback(() => setConsent(write({ analytics: true, marketing: true })), []);
  const rejectNonEssential = useCallback(() => setConsent(write({ analytics: false, marketing: false })), []);
  const save = useCallback((categories) => setConsent(write(categories)), []);
  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsent(null);
    window.dispatchEvent(new CustomEvent("capexara:consent-change", { detail: null }));
  }, []);

  const categories = consent?.categories || DEFAULT_CHOICES;

  return {
    consent,
    decided: !!consent,
    categories,
    acceptAll,
    rejectNonEssential,
    save,
    reset,
  };
}
